'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, Save, MapPin } from 'lucide-react';

interface LocationFormData {
  addressLine1: string;
  addressLine2: string;
  town: string;
  county: string;
  postcode: string;
  lat: string;
  lng: string;
  serviceArea: string;
  serviceRadius: string;
  mobileService: boolean;
}

export default function LocationTab() {
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<LocationFormData>({
    defaultValues: {
      addressLine1: '14 Industrial Estate Road',
      addressLine2: '',
      town: 'Bury St Edmunds',
      county: 'Suffolk',
      postcode: 'IP33 1AB',
      lat: '52.2437',
      lng: '0.7157',
      serviceArea: 'We cover all of West Suffolk and surrounding areas within 30 miles.',
      serviceRadius: '30',
      mobileService: false,
    },
  });

  const onSubmit = async () => {
    setIsLoading(true);
    // Backend integration point: PUT /api/businesses/{id}/location
    await new Promise((r) => setTimeout(r, 900));
    toast.success('Location details saved');
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="card-surface p-6 mb-5">
        <h3 className="font-bold text-base text-foreground mb-5 pb-3 border-b border-border">
          Registered Business Address
        </h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="loc-addr1" className="block text-sm font-semibold text-foreground mb-1.5">
              Address line 1 <span className="text-negative">*</span>
            </label>
            <input
              id="loc-addr1"
              type="text"
              className={`input-field ${errors.addressLine1 ? 'border-negative' : ''}`}
              {...register('addressLine1', { required: 'Address is required' })}
            />
            {errors.addressLine1 && <p className="text-negative text-xs mt-1">{errors.addressLine1.message}</p>}
          </div>
          <div>
            <label htmlFor="loc-addr2" className="block text-sm font-semibold text-foreground mb-1.5">
              Address line 2
            </label>
            <input id="loc-addr2" type="text" className="input-field" {...register('addressLine2')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="loc-town" className="block text-sm font-semibold text-foreground mb-1.5">
                Town / City <span className="text-negative">*</span>
              </label>
              <input
                id="loc-town"
                type="text"
                className={`input-field ${errors.town ? 'border-negative' : ''}`}
                {...register('town', { required: 'Town is required' })}
              />
              {errors.town && <p className="text-negative text-xs mt-1">{errors.town.message}</p>}
            </div>
            <div>
              <label htmlFor="loc-county" className="block text-sm font-semibold text-foreground mb-1.5">
                County
              </label>
              <input id="loc-county" type="text" className="input-field" {...register('county')} />
            </div>
          </div>
          <div className="max-w-xs">
            <label htmlFor="loc-post" className="block text-sm font-semibold text-foreground mb-1.5">
              Postcode <span className="text-negative">*</span>
            </label>
            <input
              id="loc-post"
              type="text"
              className={`input-field uppercase ${errors.postcode ? 'border-negative' : ''}`}
              {...register('postcode', { required: 'Postcode is required' })}
            />
            {errors.postcode && <p className="text-negative text-xs mt-1">{errors.postcode.message}</p>}
          </div>
        </div>
      </div>

      {/* Map preview placeholder */}
      <div className="card-surface p-6 mb-5">
        <h3 className="font-bold text-base text-foreground mb-3 pb-3 border-b border-border">
          Map Pin Position
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Confirm your business location on the map. This is shown to customers on your listing page.
        </p>
        <div className="w-full h-48 bg-muted rounded border border-border flex items-center justify-center mb-4">
          {/* Backend integration point: Google Maps embed with draggable marker */}
          <div className="text-center">
            <MapPin size={28} className="text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Map integration available</p>
            <p className="text-xs text-muted-foreground">IP33 1AB, Bury St Edmunds</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="loc-lat" className="block text-sm font-semibold text-foreground mb-1.5">Latitude</label>
            <input id="loc-lat" type="text" className="input-field font-mono text-xs" {...register('lat')} />
          </div>
          <div>
            <label htmlFor="loc-lng" className="block text-sm font-semibold text-foreground mb-1.5">Longitude</label>
            <input id="loc-lng" type="text" className="input-field font-mono text-xs" {...register('lng')} />
          </div>
        </div>
      </div>

      {/* Service area */}
      <div className="card-surface p-6 mb-6">
        <h3 className="font-bold text-base text-foreground mb-5 pb-3 border-b border-border">
          Service Area
        </h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="loc-svcarea" className="block text-sm font-semibold text-foreground mb-1.5">
              Service area description
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Describe the geographic area your business covers</p>
            <textarea
              id="loc-svcarea"
              rows={3}
              className="input-field resize-none"
              {...register('serviceArea')}
            />
          </div>
          <div>
            <label htmlFor="loc-radius" className="block text-sm font-semibold text-foreground mb-1.5">
              Service radius: {watch('serviceRadius')} miles
            </label>
            <input
              id="loc-radius"
              type="range"
              min="1"
              max="100"
              className="w-full accent-primary"
              {...register('serviceRadius')}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1 mile</span>
              <span>100 miles</span>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" className="accent-primary w-4 h-4" {...register('mobileService')} />
            <div>
              <span className="text-sm font-semibold text-foreground">Mobile / on-site service</span>
              <p className="text-xs text-muted-foreground">We travel to the customer&apos;s location</p>
            </div>
          </label>
        </div>
      </div>

      <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 -mx-6 flex justify-end gap-3">
        <button type="button" className="btn-secondary text-sm">Cancel</button>
        <button type="submit" disabled={isLoading} className="btn-primary text-sm" style={{ minWidth: 140 }}>
          {isLoading ? <><Loader2 size={14} className="animate-spin" />Saving...</> : <><Save size={14} />Save Location</>}
        </button>
      </div>
    </form>
  );
}